export interface IIndexRange {
  start: number;
  end?: number;
}
export interface ISelectIndexOption {
  index: number;
  isMulti: boolean;
  isRange: boolean;
}
export interface IIndexCache {
  /**
   * 开始选择之前的最后一次所在的索引
   * 在取消了所有选择之后的时候会使用到
   */
  lastUnselected: number;
  /**
   * 单选和多选的时候会更新这个值
   * 范围选择的时候不更新这个值
   */
  readonly lastSelected?: number;
  /**
   * 单选和多选的值
   * 可以和 rangeArray 并存 但不允许重复 重复了就无法取消了
   */
  readonly multi: number[];
  /**
   * 选择的范围
   * 可以和 multiArray 并存 但不允许重复 重复了就无法取消了
   */
  readonly ranges: IIndexRange[];
  readonly currentIndex: number;
  /**
   * 是否存在选中项
   */
  readonly hasSelected: boolean;
  isSelected(index: number): boolean;
  /**
   * 需要清除掉 multiArray 和 rangeArray
   * @param index
   */
  selectSingle(index: number): void;

  /**
   * lastSelected 如果没有值 则使用 lastUnselected 作为起始值
   * lastSelected 如果有值 则使用 lastSelected 作为起始值
   * @param index
   */
  selectRange(index: number): void;
  selectMultiRange(index: number): void;

  /**
   * 更新 single 到最新的
   * 如果是取消选中 则更新到最后一个
   * @param index
   */
  selectMulti(index: number): void;
  select(option: ISelectIndexOption): void;
  clear(): void;
}
export class IndexCache implements IIndexCache {
  lastUnselected: number;
  private lastSelectedIndex?: number;
  private multiArray: number[] = [];
  private rangeArray: IIndexRange[] = [];
  constructor(lastUnselected: number) {
    this.lastUnselected = lastUnselected;
  }
  get multi(): number[] {
    return this.multiArray;
  }
  get ranges(): IIndexRange[] {
    return this.rangeArray;
  }
  get lastSelected(): number | undefined {
    return this.lastSelectedIndex;
  }
  get hasSelected(): boolean {
    return this.multiArray.length > 0 || this.rangeArray.length > 0;
  }
  isSelected(index: number): boolean {
    if (this.multiArray.length && this.multiArray.includes(index)) {
      return true;
    }
    if (!this.rangeArray.length) {
      return false;
    }
    return this.rangeArray.some(({ start, end }) => {
      if (end) {
        return index >= start && index <= end;
      }
      return index >= start;
    });
  }
  get currentIndex(): number {
    return this.lastSelected || this.lastUnselected;
  }

  selectSingle(index: number) {
    this.multiArray = [index];
    this.rangeArray = [];
    this.lastSelectedIndex = index;
  }
  selectRange(index: number) {
    const lastIndex = this.currentIndex;
    const min = Math.min(lastIndex, index);
    const max = Math.max(lastIndex, index);
    const range = { start: min, end: max };
    this.rangeArray = [range];
  }
  selectMultiRange(index: number) {
    const { multiArray } = this;
    const lastIndex = this.currentIndex;
    const min = Math.min(lastIndex, index);
    const max = Math.max(lastIndex, index);
    const range = { start: min, end: max };
    if (multiArray.length) {
      // 如果选中的范围内包含 多选的值 需要去重 去掉在范围内的多选的值
      this.multiArray = multiArray.filter((it) => it < min && it > max);
    }
    const rangeIndex = this.rangeArray.findIndex(
      (it) => it.start === lastIndex || it.end === lastIndex
    );
    if (rangeIndex > -1) {
      this.rangeArray[rangeIndex] = range;
      return;
    }
    // 没有交集的保留
    const keep = this.rangeArray.filter(({ start, end }) => {
      return start > max || (end && end < min);
    });
    console.log(keep);
    // 左边区域 裁剪右侧
    const left = this.rangeArray
      .filter(({ start, end }) => {
        return start < min && end && end >= min && end <= max;
      })
      .map(({ start }) => {
        return { start, end: min - 1 };
      });
    // 右边区域 裁剪左侧
    const right = this.rangeArray
      .filter(({ start, end }) => {
        return start >= min && start <= max && end && end > max;
      })
      .map(({ end }) => {
        return { start: max + 1, end };
      });
    // 两边裁剪
    const both = this.rangeArray
      .filter(({ start, end }) => {
        return start < min && (!end || end > max);
      })
      .map(({ start, end }) => {
        return [
          { start, end: min - 1 },
          { start: max + 1, end },
        ];
      })
      .flat();
    this.rangeArray = [range, ...keep, ...left, ...right, ...both];
  }
  private setMulti(indexes: number[]) {
    this.multiArray = indexes;
    if (indexes.length) {
      this.lastSelectedIndex = indexes[indexes.length - 1];
    }
  }

  selectMulti(index: number) {
    const { multiArray, rangeArray, lastSelected } = this;
    if (rangeArray.length) {
      const predicate = ({ start, end }: IIndexRange) => {
        return start <= index && (!end || end >= index);
      };
      const rangeIndex = rangeArray.findIndex(predicate);

      if (rangeIndex > -1) {
        const range = rangeArray[rangeIndex];
        const { start, end } = range;
        if (start === index) {
          range.start = index + 1;
        } else if (end === index) {
          range.end = index - 1;
        } else {
          //当前选择要选择的列 已经在 范围选择之内了 所以把范围切分
          rangeArray.splice(rangeIndex, 1);
          const left = { start, end: index - 1 };
          const right = { start: index + 1, end };
          if (left.start !== left.end) {
            rangeArray.push(left);
          }
          if (right.start !== right.end) {
            rangeArray.push(right);
          }
        }
        if (index === lastSelected) {
          this.setMulti(multiArray);
        } else {
          this.lastSelectedIndex = index;
        }
        if (range.start === range.end) {
          //当前选择要选择的列 已经在 范围选择之内了 并且宽度只有一 所以把范围选择转换为 多选
          multiArray.push(range.start);
          rangeArray.splice(rangeIndex, 1);
        }
        return;
      }
    }
    if (!multiArray.length) {
      return this.setMulti([index]);
    }
    if (multiArray.includes(index)) {
      return this.setMulti(multiArray.filter((it) => it !== index));
    }
    multiArray.push(index);
    this.lastSelectedIndex = index;
  }
  select(option: ISelectIndexOption) {
    const { index, isMulti, isRange } = option;
    if (isRange) {
      return this.selectMultiRange(index);
    }
    if (isMulti) {
      return this.selectMulti(index);
    }
    this.selectSingle(index);
  }
  clear(): void {
    if (this.multiArray.length) {
      this.multiArray = [];
    }
    if (this.rangeArray.length) {
      this.rangeArray = [];
    }
    this.lastSelectedIndex = undefined;
  }
}
